"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useParams } from "next/navigation";
import Sidebar from "./Sidebar";
import SidebarInner from "./SidebarInner";
import "@/styles/header.css";
import "@/styles/globals.css";

export default function HeaderInner({
  brandData,
  propertyData,
  brandId,
  backgroundColor,
  propertyId,
  onClick,
}) {
  const [hasMounted, setHasMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const pathname = usePathname();
  const { propertySlug, citySlug } = useParams();
  const [headerApiData, setHeaderApiData] = useState([]);
  const [brandSlug, setBrandSlug] = useState("");

  const handleBookNowClick = () => {
    onClick();
  };

  const headerImagesUrl = {
    1: "/images/headerLogos/MastiffLogo.png",
    2: "/images/headerLogos/KyriadLogo.png",
    3: "/images/headerLogos/CitrusLogo.png",
    5: "/images/tab-logo6.png",
    6: "/images/tab-logo5.png",
  };

  const headerLogo = headerImagesUrl[brandId] || "/logo.png";

  useEffect(() => {
    const fetchHeaderApi = async () => {
      try {
        if (!propertyId) {
          return;
        }
        const experiencesRes = await fetch(
          `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/property/menu?propertyId=${propertyId}`
        );
        const headerResult = await experiencesRes.json();
        if (
          headerResult.errorCode === "0" &&
          Array.isArray(headerResult?.data)
        ) {
          setHeaderApiData(headerResult?.data);
        } else {
          setHeaderApiData([]);
        }
      } catch (error) {
        console.error("Error in data fetch:", error);
        setHeaderApiData([]);
      }
    };
    fetchHeaderApi();
  }, [propertyId]);

  useEffect(() => {
    setHasMounted(true);
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    const onScroll = () => setIsScrolled(window.scrollY > 150);
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!brandData || !propertyData) return;
    const brandSlug =
      brandData.find(
        (brand) => brand.hotelBrandId === propertyData.hotelBrandId
      )?.brandSlug || "";
    setBrandSlug(brandSlug);
  }, [brandData, propertyData]); // <-- Make sure BOTH are dependencies

  if (!hasMounted) return null;
  // if (!brandSlug) return null;

  // ✅ Pages where only sidebar should be shown
  const sidebarOnlyPages = ["/"];
  const showSidebarOnly = sidebarOnlyPages.includes(pathname);

  return (
    <header>
      {/* Sidebar */}
      {isMobile ? (
        <SidebarInner
          brandData={brandData}
          propertyData={propertyData}
          headerApiData={headerApiData}
          isOpen={menuOpen || showSidebarOnly}
          setIsOpen={setMenuOpen}
          forceOpen={showSidebarOnly}
          onClick={onClick}
        />
      ) : (
        <Sidebar
          headerApiData={headerApiData}
          isOpen={menuOpen || showSidebarOnly}
          setIsOpen={setMenuOpen}
          forceOpen={showSidebarOnly}
          onClick={onClick}
        />
      )}
      {/* ============ VERSION 1: Sidebar Only (Home, Offers, Contact) ============ */}
      {showSidebarOnly ? (
        <nav
          className={`navbar navbar-expand-md navbar-light fixed-top ${
            isScrolled ? "navbar-scroll bg-white shadow-sm" : ""
          }`}
        >
          <div className="container-fluid d-flex align-items-center justify-content-between">
            {/* Left placeholder */}
            <div style={{ width: "100px" }}></div>

            {/* Center - Logo */}
            <Link href="/" className="logoimg mx-auto">
              <Image
                src={headerLogo}
                alt="Logo"
                width={80}
                height={30}
                fetchPriority="high"
              />
            </Link>

            {/* Right - Book Now */}
            <div style={{ width: "100px" }} className="text-end">
              <a
                className="box-btn know-more p-2 book-btn cursor-pointer me-2"
                href="#"
                onClick={handleBookNowClick}
                style={{ background: backgroundColor }}
              >
                Book Now
              </a>
            </div>
          </div>
        </nav>
      ) : (
        /* ============ VERSION 2: Full Width Menu + Sidebar (Other Pages) ============ */
        <nav
          className={`navbar navbar-expand-md navbar-light fixed-top ${
            isScrolled ? "navbar-scroll bg-white shadow-sm" : ""
          }`}
        >
          <div className="container-fluid d-flex align-items-center justify-content-between pe-1 pe-md-1">
            {/* Left - Sidebar Toggle (mobile) */}

            <button
              className="clarks-sidebar-toggler d-md-none"
              onClick={() => setMenuOpen(true)}
              aria-label="Toggle Sidebar"
            >
              <span />
              <span />
              <span />
            </button>
            {/* Center - Logo */}
            <Link
              href="/"
              className="logoimg d-md-none"
              style={{ marginLeft: "135px" }}
            >
              <Image
                src={headerLogo}
                alt="Logo"
                width={80}
                height={30}
                fetchPriority="high"
              />
            </Link>

            <div className="text-end header-book-now-btn book-now-parent me-0">
              <a
                className="box-btn know-more p-2 book-btn cursor-pointer me-2"
                href="#"
                onClick={handleBookNowClick}
                style={{ background: backgroundColor }}
              >
                Book Now
              </a>
            </div>

            {/* Full-width Menu (desktop only) */}
            {/* Full-width Menu (desktop only, with centered logo) */}
            <div className="mainDiv">
              {/* Left links */}
              <div className="leftDiv">
                <div className="leftLinks">
                  <Link href={`/`} className="nav-link flex items-center gap-2">
                    <Image
                      src="/gif/loader3.gif"
                      width={100}
                      height={50}
                      alt="gif"
                      className="ms-5"
                    />
                  </Link>
                  <Link
                    href={`/${brandSlug}/${propertySlug}/${citySlug}`}
                    className="nav-link"
                  >
                    Overview
                  </Link>
                  {headerApiData?.[0]?.room && (
                    <Link
                      href={`/${brandSlug}/${propertySlug}/${citySlug}/rooms`}
                      className="nav-link"
                    >
                      Rooms
                    </Link>
                  )}
                  {headerApiData?.[0]?.event && (
                    <Link
                      href={`/${brandSlug}/${propertySlug}/${citySlug}/events`}
                      className="nav-link"
                    >
                      Events
                    </Link>
                  )}
                  {headerApiData?.[0]?.dine && (
                    <Link
                      href={`/${brandSlug}/${propertySlug}/${citySlug}/dining`}
                      className="nav-link"
                    >
                      Dining
                    </Link>
                  )}
                </div>
              </div>

              {/* Center Logo */}
              <div className="centerLogo">
                <Link href="/" className="logoimg">
                  <Image
                    src={headerLogo}
                    alt="Logo"
                    width={80}
                    height={30}
                    fetchPriority="high"
                  />
                </Link>
              </div>

              {/* Right links */}
              <div className="rightDiv">
                <div className="rightLinks">
                  {headerApiData?.[0]?.offer && (
                    <Link
                      href={`/${brandSlug}/${propertySlug}/${citySlug}/offers`}
                      className="nav-link"
                    >
                      Offers
                    </Link>
                  )}
                  {headerApiData?.[0]?.gallery && (<Link
                    href={`/${brandSlug}/${propertySlug}/${citySlug}/gallery`}
                    className="nav-link"
                  >
                    Gallery
                  </Link>)}
                  <Link
                    href={`/${brandSlug}/${propertySlug}/${citySlug}/contact`}
                    className="nav-link"
                  >
                    Contact
                  </Link>
                </div>

                <div className="d-flex align-items-center gap-2 text-end header-book-now-btn me-2">
                  {/* <Link href="#" className="nav-link me-2">
                    Login/Join
                  </Link> */}

                  {/* Empty spacer */}
                  {!isScrolled && <div style={{ marginRight: "80px" }}></div>}

                  <a
                    className={`box-btn know-more p-2 book-btn cursor-pointer ${
                      isScrolled ? "" : "hidden"
                    }`}
                    href="#"
                    onClick={handleBookNowClick}
                    style={{ background: backgroundColor || "#f04e02" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
